import { useEffect, useState } from "react";
import type { BeatGrid } from "./beatSync";

type TokenProvider = {
  getIdToken: () => Promise<string>;
};

// One request per track per session: a track's beat never changes
const grids = new Map<string, Promise<BeatGrid | null>>();

export function fetchMusicBeatGrid(musicPath: string, currentUser: TokenProvider): Promise<BeatGrid | null> {
  const cached = grids.get(musicPath);
  if (cached) return cached;
  const request = (async () => {
    const token = await currentUser.getIdToken();
    const response = await fetch(`${process.env.REACT_APP_API_BASE}/carousel-video/music-beat-grid`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ musicPath }),
    });
    if (!response.ok) throw new Error(`Beat grid request failed: ${response.status}`);
    return ((await response.json()).grid ?? null) as BeatGrid | null;
  })();
  grids.set(musicPath, request);
  // A failed request plays the video unsynced now and is retried next time
  request.catch(() => grids.delete(musicPath));
  return request;
}

/**
 * The beat grid the preview should cut on: the track's, unless the video is
 * muted or has no music. null until it loads, or when the track has no steady beat.
 */
export function useMusicBeatGrid(
  musicPath: string | undefined,
  muted: boolean,
  currentUser: TokenProvider | null | undefined
): BeatGrid | null {
  const [grid, setGrid] = useState<BeatGrid | null>(null);
  useEffect(() => {
    setGrid(null);
    if (!musicPath || muted || !currentUser) return;
    let cancelled = false;
    fetchMusicBeatGrid(musicPath, currentUser)
      .then((g) => { if (!cancelled) setGrid(g); })
      .catch((error) => console.warn("Playing the preview without beat sync:", error));
    return () => { cancelled = true; };
  }, [musicPath, muted, currentUser]);
  return grid;
}
